// openEHR RM model — BMM package org.openehr.rm.data_structures.item_structure
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { DV_TEXT } from "../data_types/text.ts";
import { DATA_STRUCTURE } from "./data_structures.ts";
import { CLUSTER, ELEMENT } from "./representation.ts";
import type { ITEM } from "./representation.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * Abstract parent class of all spatial data types.
 */
export abstract class ITEM_STRUCTURE extends DATA_STRUCTURE {
}

/**
 * Logical tree data structure. The tree may be empty. Used for representing data which are logically a tree such as audiology results, microbiology results, biochemistry results.
 */
export class ITEM_TREE extends ITEM_STRUCTURE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * The items comprising the \`ITEM_TREE\`. Can include 0 or more \`CLUSTERs\` and/or 0 or more individual \`ELEMENTs\`.
   */
  items?: ITEM[];
  /**
   * True if path  a_path' is a valid leaf path.
   *
   * @param a_path - Parameter
   * @returns Result value
   */
  has_element_path(a_path: openehr_base.String | string): openehr_base.Boolean {
    const element = this.element_at_path(a_path);
    return openehr_base.Boolean.from(element !== undefined);
  }

  /**
   * Return the leaf element at the path  a_path'.
   * @param a_path - Parameter
   * @returns Result value
   */
  element_at_path(a_path: openehr_base.String | string): ELEMENT | undefined {
    const item = this.item_at_path(a_path);
    if (item instanceof ELEMENT) {
      return item;
    }
    return undefined;
  }

  /**
   * Generate a CEN EN13606-compatible hierarchy, which is the same as the tree's physical representation.
   * @returns Result value
   */
  override as_hierarchy(): CLUSTER {
    const result = new CLUSTER();
    result.archetype_node_id = this.archetype_node_id;
    result.name = this.name;
    (result as any).items = this.items || [];
    return result;
  }
}

/**
 * Logical single value data structure. Used to represent any data which is logically a single value, such as a person's height or weight.
 */
export class ITEM_SINGLE extends ITEM_STRUCTURE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  item?: ELEMENT;
  /**
   * Generate a CEN EN13606-compatible hierarchy consisting of a single \`ELEMENT\`.
   * @returns Result value
   */
  override as_hierarchy(): ELEMENT {
    // For ITEM_SINGLE, the hierarchy is just the single element itself
    if (!this.item) {
      throw new Error("ITEM_SINGLE.as_hierarchy: item is not set");
    }
    return this.item;
  }
}

/**
 * Logical relational database style table data structure, in which columns are named and ordered with respect to each other. Implemented using Cluster-per-row encoding. Each row Cluster must have an identical number of Elements, each of which in turn must have identical names and value types in the corresponding positions in each row.
 *
 * Some columns may be designated  key' columns, containing key data for each row, in the manner of relational tables. This allows row-naming, where each row represents a body site, a blood antigen etc. All values in a column have the same data type.
 *
 * Used for representing any data which is logically a table of values, such as blood pressure, most protocols, many blood tests etc.
 *
 * Misuse: Not to be used for time-based data, which should be represented with the temporal class \`HISTORY\`. The table may be empty.
 */
export class ITEM_TABLE extends ITEM_STRUCTURE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Physical representation of the table as a list of \`CLUSTERs\`, each containing the data of one row of the table.
   */
  rows?: CLUSTER[];
  /**
   * Number of rows in the table.
   * @returns Result value
   */
  row_count(): openehr_base.Integer {
    return openehr_base.Integer.from(this.rows?.length || 0);
  }

  /**
   * Return number of columns in the table.
   * @returns Result value
   */
  column_count(): openehr_base.Integer {
    // Number of columns is determined by items in first row
    if (!this.rows || this.rows.length === 0) return openehr_base.Integer.from(0);
    const items = (this.rows[0] as any).items as ITEM[] | undefined;
    return openehr_base.Integer.from(items?.length || 0);
  }

  /**
   * Return set of row names.
   * @returns Result value
   */
  row_names(): DV_TEXT[] {
    if (!this.rows) return [];
    return this.rows.map(row => row.name).filter((name): name is DV_TEXT => name !== undefined);
  }

  /**
   * Return set of column names.
   * @returns Result value
   */
  column_names(): DV_TEXT[] {
    if (!this.rows || this.rows.length === 0) return [];
    const items = (this.rows[0] as any).items as ITEM[] | undefined;
    if (!items) return [];
    return items.map(item => item.name).filter((name): name is DV_TEXT => name !== undefined);
  }

  /**
   * Return i-th row.
   * @param i - Parameter
   * @returns Result value
   */
  ith_row(i: openehr_base.Integer | number): CLUSTER | undefined {
    const index = typeof i === "number" ? i : (i.value || 0);
    if (!this.rows) return undefined;
    return this.rows[index - 1]; // 1-based indexing
  }

  /**
   * Return \`True\` if there is a column with name = \`_a_key_\`.
   * @param a_key - Parameter
   * @returns Result value
   */
  has_row_with_name(a_key: openehr_base.String | string): openehr_base.Boolean {
    const key = typeof a_key === "string" ? a_key : (a_key.value || "");
    if (!this.rows) return openehr_base.Boolean.from(false);
    return openehr_base.Boolean.from(this.rows.some(row => row.name?.value === key));
  }

  /**
   * Return \`True\` if there is a column with name = \`_a_key_\`.
   * @param a_key - Parameter
   * @returns Result value
   */
  has_column_with_name(a_key: openehr_base.String | string): openehr_base.Boolean {
    const key = typeof a_key === "string" ? a_key : (a_key.value || "");
    const colNames = this.column_names();
    return openehr_base.Boolean.from(colNames.some(name => name.value === key));
  }

  /**
   * Return row with name = \`_a_key_\`.
   * @param a_key - Parameter
   * @returns Result value
   */
  named_row(a_key: openehr_base.String | string): CLUSTER | undefined {
    const key = typeof a_key === "string" ? a_key : (a_key.value || "");
    if (!this.rows) return undefined;
    return this.rows.find(row => row.name?.value === key);
  }

  /**
   * Return \`True\` if there is a row with key \`_keys_\`.
   * @param keys - Parameter
   * @returns Result value
   */
  has_row_with_key(keys: string[]): openehr_base.Boolean {
    return openehr_base.Boolean.from(this.row_with_key(keys) !== undefined);
  }

  /**
   * Return rows with particular keys.
   * @param keys - Parameter
   * @returns Result value
   */
  row_with_key(keys: string[]): CLUSTER | undefined {
    if (!this.rows || !keys || keys.length === 0) return undefined;
    return this.rows.find(row => {
      const items = (row as any).items as ITEM[] | undefined;
      if (!items) return false;
      return keys.every((key, idx) => {
        const item = items[idx] as ELEMENT | undefined;
        return item?.value && (item.value as any).value === key;
      });
    });
  }

  /**
   * Return cell at a particular location.
   * @param i - Parameter
   * @param j - Parameter
   * @returns Result value
   */
  element_at_cell_ij(
    i: openehr_base.Integer | number,
    j: openehr_base.Integer | number,
  ): ELEMENT | undefined {
    const rowIdx = typeof i === "number" ? i : (i.value || 0);
    const colIdx = typeof j === "number" ? j : (j.value || 0);
    const row = this.ith_row(rowIdx);
    if (!row) return undefined;
    const items = (row as any).items as ITEM[] | undefined;
    if (!items) return undefined;
    return items[colIdx - 1] as ELEMENT | undefined; // 1-based indexing
  }

  /**
   * Generate a CEN EN13606-compatible hierarchy consisting of a single \`CLUSTER\` containing the \`CLUSTERs\` representing the columns of this table.
   * @returns Result value
   */
  override as_hierarchy(): CLUSTER {
    const result = new CLUSTER();
    result.archetype_node_id = this.archetype_node_id;
    result.name = this.name;
    (result as any).items = this.rows || [];
    return result;
  }
}

/**
 * Logical list data structure, where each item has a value and can be referred to by a name and a positional index in the list. The list may be empty.
 *
 * \`ITEM_LIST\` is used to represent any data which is logically a list of values, such as blood pressure, most protocols, many blood tests etc.
 *
 * Not to be used for time-based lists, which should be represented with the proper temporal class, i.e. \`HISTORY\`.
 */
export class ITEM_LIST extends ITEM_STRUCTURE {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Physical representation of the list.
   */
  items?: ELEMENT[];
  /**
   * Count of all items.
   * @returns Result value
   */
  item_count(): openehr_base.Integer {
    return openehr_base.Integer.from(this.items?.length || 0);
  }

  /**
   * Retrieve the names of all items.
   * @returns Result value
   */
  names(): DV_TEXT[] {
    if (!this.items) return [];
    return this.items.map(item => item.name).filter((name): name is DV_TEXT => name !== undefined);
  }

  /**
   * Retrieve the item with name ‘a_name’.
   * @param a_name - Parameter
   * @returns Result value
   */
  named_item(a_name: openehr_base.String | string): ELEMENT | undefined {
    const name = typeof a_name === "string" ? a_name : (a_name.value || "");
    if (!this.items) return undefined;
    return this.items.find(item => item.name?.value === name);
  }

  /**
   * Retrieve the i-th item with name.
   * @param i - Parameter
   * @returns Result value
   */
  ith_item(i: openehr_base.Integer | number): ELEMENT | undefined {
    const index = typeof i === "number" ? i : (i.value || 0);
    if (!this.items) return undefined;
    return this.items[index - 1]; // 1-based indexing
  }

  /**
   * Generate a CEN EN13606-compatible hierarchy consisting of a single \`CLUSTER\` containing the \`ELEMENTs\` of this list.
   * @returns Result value
   */
  override as_hierarchy(): CLUSTER {
    const result = new CLUSTER();
    result.archetype_node_id = this.archetype_node_id;
    result.name = this.name;
    (result as any).items = this.items || [];
    return result;
  }
}
